/**
 * ============================================================================
 *  File        : frontend/src/pages/CaseItemsPage.tsx
 *  Module      : pages
 *  Purpose     : /cases/:id/items 品項明細（spec §8.2 / §11.2 畫面 2）—
 *                列出案件品項（getCase detail）+ 逐品編輯（PUT item，價格版本
 *                由後端自動寫入）。
 *  Author      : EF Engineering
 *  Created     : 2026-06-02
 *  Updated     : 2026-06-02
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端：GET /api/v1/cases/:id（含 items）；PUT /api/v1/cases/:case_id/items/:item_id
 *  （v13_listing::update_item，Idempotency-Key）。
 *  RBAC：讀 = marketplace.cases.view（CaseLayout 已 gate）；編輯 =
 *        marketplace.cases.update（PERMISSIONS.caseManage，後端最終權威）。
 *  業務不變式：Closed_Locked 後品項不可修改（DB trigger 阻擋 UPDATE；前端在
 *        ClosedLocked 時隱藏編輯入口並顯示鎖定提示，避免使用者撞 409）。
 *  ⚠️ 単価/重量一律字串型 Decimal，前端不得轉 float（CLAUDE.md §2）。空欄位 = 不送該欄。
 */

import { useState, type ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import {
  getCase,
  updateCaseItem,
  type CaseItemDto,
  type UpdateCaseItemDto,
} from '../api/cases'
import { ApiError } from '../api/client'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import { toast } from '../components/ui/sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'

interface PageProps {
  t: (key: string) => string
}

/** Closed_Locked 後業務資料不可修改（CLAUDE.md §2 invariant 5）。 */
const LOCKED_STATE = 'ClosedLocked'

function errDetail(error: unknown): string {
  return error instanceof ApiError ? `（${error.message}）` : ''
}

export function CaseItemsPage({ t }: PageProps): ReactElement {
  const params = useParams()
  const caseId = params.id ?? ''
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can(PERMISSIONS.caseManage)
  const [editing, setEditing] = useState<CaseItemDto | null>(null)

  const query = useQuery({
    queryKey: ['case', caseId],
    queryFn: () => getCase(caseId),
    enabled: caseId !== '',
  })

  if (query.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (query.isError || !query.data) {
    return (
      <p className="text-sm text-destructive">
        {t('case.items.loadError')}
        {errDetail(query.error)}
      </p>
    )
  }

  const { items, ...c } = query.data
  const isLocked = (c.workflow_state ?? '') === LOCKED_STATE

  const closeDialog = (): void => setEditing(null)
  const onSaved = (): void => {
    closeDialog()
    void queryClient.invalidateQueries({ queryKey: ['case', caseId] })
  }

  return (
    <div className="space-y-4">
      <header className="page-heading">
        <span className="eyebrow">{t('case.stage.items')}</span>
        <h2 className="text-lg font-semibold">{t('case.items.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('case.items.subtitle')}</p>
      </header>

      {isLocked ? (
        <Badge variant="outline" data-testid="items-locked">
          {t('case.items.lockedHint')}
        </Badge>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            {t('case.items.title')}（{items.length}）
          </CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('case.items.empty')}</p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('case.detail.lineNo')}</TableHead>
                    <TableHead>{t('field.itemName')}</TableHead>
                    <TableHead className="text-right">{t('field.estimatedWeight')}</TableHead>
                    <TableHead className="text-right">{t('field.minUnitPrice')}</TableHead>
                    <TableHead className="text-right">{t('field.targetUnitPrice')}</TableHead>
                    <TableHead>{t('field.uom')}</TableHead>
                    <TableHead className="text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} data-testid={`item-row-${item.id}`}>
                      <TableCell>{item.line_no}</TableCell>
                      <TableCell className="font-medium">{item.item_name}</TableCell>
                      <TableCell className="text-right">
                        {item.estimated_weight_kg ?? '—'}
                      </TableCell>
                      <TableCell className="text-right">{item.min_unit_price ?? '—'}</TableCell>
                      <TableCell className="text-right">{item.target_unit_price ?? '—'}</TableCell>
                      <TableCell>{item.uom}</TableCell>
                      <TableCell className="text-right">
                        {canEdit && !isLocked ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`item-edit-${item.id}`}
                            onClick={() => setEditing(item)}
                          >
                            {t('case.items.editAction')}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={editing !== null} onOpenChange={(open) => (open ? null : closeDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('case.items.editTitle')}</DialogTitle>
            <DialogDescription>{t('case.items.subtitle')}</DialogDescription>
          </DialogHeader>
          {editing ? (
            <EditItemForm
              t={t}
              caseId={caseId}
              item={editing}
              onCancel={closeDialog}
              onSaved={onSaved}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---- 編輯表單（react-hook-form + zod）-------------------------------------

/** 把 Decimal 字串 trim 後回傳；空字串 → undefined（= 不送該欄）。 */
function optDecimal(value: string): string | undefined {
  const text = value.trim()
  return text === '' ? undefined : text
}

function EditItemForm({
  t,
  caseId,
  item,
  onCancel,
  onSaved,
}: {
  t: (key: string) => string
  caseId: string
  item: CaseItemDto
  onCancel: () => void
  onSaved: () => void
}): ReactElement {
  // Decimal 欄位接受空字串（= 不變）或可解析數字字串；前端不轉 float（僅校驗格式）。
  const decimalField = z
    .string()
    .trim()
    .optional()
    .default('')
    .refine((v) => v === '' || Number.isFinite(Number(v)), {
      message: t('case.items.error'),
    })

  const schema = z.object({
    description: z.string().trim().min(1, t('common.required')),
    estimatedWeightKg: decimalField,
    minPrice: decimalField,
    targetPrice: decimalField,
    packageForm: z.string().trim().optional().default(''),
    reason: z.string().trim().optional().default(''),
  })

  type FormValues = z.input<typeof schema>

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: item.item_name,
      estimatedWeightKg: item.estimated_weight_kg ?? '',
      minPrice: item.min_unit_price ?? '',
      targetPrice: item.target_unit_price ?? '',
      packageForm: '',
      reason: '',
    },
  })

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const body: UpdateCaseItemDto = {
        description: values.description?.trim() || undefined,
        estimated_weight_kg: optDecimal(values.estimatedWeightKg ?? ''),
        min_price: optDecimal(values.minPrice ?? ''),
        target_price: optDecimal(values.targetPrice ?? ''),
        package_form: (values.packageForm ?? '').trim() || undefined,
        reason: (values.reason ?? '').trim() || undefined,
      }
      return updateCaseItem(caseId, item.id, body)
    },
    onSuccess: () => {
      toast.success(t('case.items.success'))
      onSaved()
    },
    onError: (error) => toast.error(`${t('case.items.error')}${errDetail(error)}`),
  })

  const submit = form.handleSubmit((values) => mutation.mutate(values))

  return (
    <form className="space-y-3" onSubmit={(e) => void submit(e)}>
      <Field
        label={t('field.itemName')}
        error={form.formState.errors.description?.message}
      >
        <Input
          data-testid="item-edit-description"
          aria-invalid={!!form.formState.errors.description}
          {...form.register('description')}
        />
      </Field>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field
          label={t('field.estimatedWeight')}
          error={form.formState.errors.estimatedWeightKg?.message}
        >
          <Input
            inputMode="decimal"
            data-testid="item-edit-weight"
            {...form.register('estimatedWeightKg')}
          />
        </Field>
        <Field label={t('field.packageForm')} hint={t('case.items.packageHint')}>
          <Input
            data-testid="item-edit-package"
            placeholder={t('option.packFlexible')}
            {...form.register('packageForm')}
          />
        </Field>
        <Field
          label={t('field.minUnitPrice')}
          error={form.formState.errors.minPrice?.message}
        >
          <Input
            inputMode="decimal"
            data-testid="item-edit-min-price"
            {...form.register('minPrice')}
          />
        </Field>
        <Field
          label={t('field.targetUnitPrice')}
          error={form.formState.errors.targetPrice?.message}
        >
          <Input
            inputMode="decimal"
            data-testid="item-edit-target-price"
            {...form.register('targetPrice')}
          />
        </Field>
      </div>

      <Field label={t('field.reason')}>
        <Input
          data-testid="item-edit-reason"
          placeholder={t('case.items.reasonHint')}
          {...form.register('reason')}
        />
      </Field>

      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" data-testid="item-edit-submit" disabled={mutation.isPending}>
          {mutation.isPending ? t('common.loading') : t('common.save')}
        </Button>
      </DialogFooter>
    </form>
  )
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string
  error?: string
  hint?: string
  children: ReactElement
}): ReactElement {
  return (
    <label className="space-y-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </label>
  )
}
